class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.ensureReady();
  }

  ensureReady() {
    this.refreshInputRefs();
    this.bindInputListeners();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    if (!this.predictiveSearchResults) return false;

    if (!this._predictiveLoadingMarkup) {
      this._predictiveLoadingMarkup = this.predictiveSearchResults.innerHTML;
    }

    if (!this.input) return false;

    this.setupEventListeners();
    this.syncQueryState();
    return true;
  }

  setupEventListeners() {
    if (!this.input?.form || this._predictiveListenersBound) return;
    this._predictiveListenersBound = true;

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onQueryInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultsClick.bind(this));
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  onQueryInput() {
    this.syncQueryState();
  }

  syncQueryState() {
    const hasQuery = this.getQuery().length > 0;

    if (hasQuery) {
      this.setAttribute('data-has-query', '');
      return;
    }

    this.removeAttribute('data-has-query');
  }

  onResultsClick(event) {
    const searchForButton = event.target.closest(
      '.nodus-search__search-for-button, .predictive-search__search-for-button'
    );
    if (!searchForButton || !this.input?.form) return;

    event.preventDefault();
    this.input.form.requestSubmit();
  }

  isPredictiveActive() {
    return this.hasAttribute('loading') || this.hasAttribute('open') || this.hasAttribute('results');
  }

  notifyStateChange() {
    this.dispatchEvent(new CustomEvent('predictive-search:state-change', { bubbles: true }));
  }

  resetPredictiveShell() {
    if (this.predictiveSearchResults && this._predictiveLoadingMarkup) {
      this.predictiveSearchResults.innerHTML = this._predictiveLoadingMarkup;
    }
  }

  onChange() {
    if (!this.ensureReady()) return;

    super.onChange();
    this.syncQueryState();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    if (!this.ensureReady()) return;

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.hasAttribute('results')) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    try {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    } catch (error) {
      return;
    }
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  parseSectionMarkup(text, sectionId) {
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    const sectionNode = parsed.querySelector(`#shopify-section-${sectionId}`);
    return {
      parsed,
      sectionNode,
      markup: sectionNode ? sectionNode.innerHTML : '',
    };
  }

  sectionHasProducts(sectionNode) {
    return Boolean(
      sectionNode?.querySelector('#predictive-search-results-products-list .predictive-search__list-item')
    );
  }

  fetchSearchFallbackMarkup(searchTerm) {
    const searchUrl = (window.routes && window.routes.search_url) || '/search';
    const url = `${searchUrl}?q=${encodeURIComponent(searchTerm)}&type=product&section_id=nodus-search-fallback&options[prefix]=last`;

    return fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => this.parseSectionMarkup(text, 'nodus-search-fallback').markup)
      .catch(() => '');
  }

  resolveResultsMarkup(searchTerm, predictiveMarkup, predictiveSectionNode) {
    if (this.sectionHasProducts(predictiveSectionNode)) {
      return Promise.resolve(predictiveMarkup);
    }

    return this.fetchSearchFallbackMarkup(searchTerm).then((fallbackMarkup) => {
      if (!fallbackMarkup.trim()) {
        return predictiveMarkup;
      }

      const parsed = new DOMParser().parseFromString(fallbackMarkup, 'text/html');
      const hasFallbackProducts = parsed.querySelector(
        '#predictive-search-results-products-list .predictive-search__list-item'
      );

      return hasFallbackProducts ? fallbackMarkup : predictiveMarkup;
    });
  }

  getSearchResults(searchTerm) {
    if (!this.ensureReady()) return;

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.resetPredictiveShell();

    const predictiveSearchUrl =
      (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const url =
      `${predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}` +
      '&section_id=predictive-search' +
      '&resources[type]=product,query' +
      '&resources[limit]=10' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku' +
      '&resources[options][unavailable_products]=hide';

    fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const { markup: predictiveMarkup, sectionNode: predictiveSectionNode } = this.parseSectionMarkup(
          text,
          'predictive-search'
        );

        return this.resolveResultsMarkup(searchTerm, predictiveMarkup, predictiveSectionNode);
      })
      .then((resultsMarkup) => {
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        if (resultsMarkup.trim().length === 0) {
          this.renderNoResults(searchTerm);
          return;
        }

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        this.renderNoResults(searchTerm);
        console.error('Predictive search failed:', error);
      });
  }

  renderNoResults(searchTerm) {
    if (!this.predictiveSearchResults) return;

    const template =
      (window.accessibilityStrings && window.accessibilityStrings.noResults) || 'No results for "[terms]"';
    const escapedTerm = searchTerm
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const message = template.replace('[terms]', escapedTerm);

    this.predictiveSearchResults.innerHTML = `
      <div class="nodus-search__predictive-empty">
        <p class="nodus-search__predictive-empty-text">${message}</p>
      </div>
    `;
    this.setAttribute('results', '');
    this.removeAttribute('loading');
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', '');
    this.open();
    this.notifyStateChange();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || !statusText) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', '');

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion?.textContent) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    const drawerBody = this.querySelector('.nodus-search__body');
    if (drawerBody) {
      this.resultsMaxHeight = drawerBody.clientHeight;
      return this.resultsMaxHeight;
    }

    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom ?? 0;
    this.resultsMaxHeight = Math.max(window.innerHeight - headerBottom, 240);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    if (this.classList.contains('nodus-search__shell')) {
      this.predictiveSearchResults.style.maxHeight = '';
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', '');
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.notifyStateChange();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = '';
      this.removeAttribute('results');
      this.removeAttribute('data-has-query');
      this.resetPredictiveShell();
      this.searchTerm = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
    this.notifyStateChange();
  }
}

customElements.define('predictive-search', PredictiveSearch);
